import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  booleanAttribute,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Button,
  FeedbackBanner,
  Icon,
  InputControl,
  Modal,
  SearchInput,
  Tab,
  Tabs,
} from '@sisoma/ui';
import { Subject, debounceTime } from 'rxjs';

import { apiErrorMessage } from './api-error';
import { type MediaKind } from './cloudinary-upload.service';
import {
  MediaLibraryService,
  SOURCE_LABELS,
  type LibraryItem,
  type LibrarySource,
  type MediaPick,
} from './media-library.service';
import { ACCEPTED_TYPES, MAX_FILE_BYTES, megabytes } from './media-rules';

/**
 * The cap on attachments, counting what is already on the item —
 * `MediaModalComponent.addSelectedMedia`'s
 * `mediaLength + this.currentLength + 1 > 10` (media-modal.component.ts:148-153).
 *
 * The creator screen never actually passes `currentLength`, so its cap silently
 * ignores already-attached media; the drawer passes it, which is the rule as
 * written rather than the bug as shipped.
 */
export const MAX_MEDIA_ITEMS = 10;

/** The Upload tab plus every library tab. */
type TabId = 'upload' | LibrarySource;

/** How long the search box waits before committing a term. */
export const SEARCH_DEBOUNCE_MS = 400;

/** The legacy drop zone's own wording (add-media.component.html:11-21). */
const HINTS: Readonly<Record<MediaKind, string>> = {
  image: 'JPG or PNG file',
  video: 'Mp4 file or Mov',
  document: 'pdf, doc, docx or ppt',
};

const KIND_NOUNS: Readonly<Record<MediaKind, string>> = {
  image: 'image',
  video: 'video',
  document: 'document',
};

/** "Powered By {{ provider }}" — media-library.component.html:34-36. */
const CREDITS: Readonly<Partial<Record<TabId, string>>> = {
  pexels: 'Powered by Pexels',
  unsplash: 'Powered by Unsplash',
  pixabay: 'Powered by Pixabay',
  giphy: 'Powered by Giphy',
};

/** One chosen file, with the object URL its thumbnail is drawn from. */
interface PickedFile {
  readonly file: File;
  /** A `blob:` URL for images, `null` for everything else. */
  readonly previewUrl: string | null;
}

/** One row of the Selected strip, whichever tab put it there. */
interface SelectedEntry {
  readonly key: string;
  readonly name: string;
  readonly thumbUrl: string | null;
  /** Drawn when there is no thumbnail — a video file or a document has none. */
  readonly glyph: 'file-text' | 'video' | 'image';
  /** Which list `remove` has to take it out of. */
  readonly list: 'files' | 'library';
  readonly index: number;
}

/**
 * "Add Media" / "Add Document" — the content drawer's media picker.
 *
 * ## The tab set is legacy's, as the *content* context offers it
 *
 * `MediaModalComponent`'s template gates its tabs on `isDocument` and
 * `designType` (media-modal.component.html:5-167), and `crud-content` sets both
 * from the post type (crud-content.component.ts:400-405). That leaves three
 * shapes, reproduced here:
 *
 * | post type  | tabs                                                |
 * | ---------- | --------------------------------------------------- |
 * | image      | Upload · My Media · Pexels · UnSplash · Pixabay      |
 * | video      | Upload · My Media · Gifs by Giphy                    |
 * | document   | Upload · My Media                                    |
 *
 * My Media is ungated — its `@if (designType !== 'video')` is commented out
 * (media-modal.component.html:28, :53) — so it shows for all three, documents
 * included. **Designs is not built**: that tab is commented out in the legacy
 * template too (media-modal.component.html:119-145), its `previewImage` fetch is
 * commented out in the component (media-modal.component.ts:121-130), and the
 * drawer's design flow rides the design context rather than an attachment.
 * Stock **video** appears nowhere in the content context: the photo tabs are
 * hidden for video posts and the API's Pixabay and Unsplash providers report
 * `supportsVideo = false` anyway.
 *
 * ## One pick or many
 *
 * The dialog accumulates by default and stops at {@link MAX_MEDIA_ITEMS}. Two
 * things make it singular instead: a `document` kind, which is legacy's own rule
 * (`isDocument`), and the {@link single} input, for a caller whose pick is one
 * thing by nature — the content drawer's design card, whose "+" sets the
 * design's feature image and attaches nothing. Both take the same
 * {@link singleSelect} path: the file input drops `multiple`, the cap is one,
 * and picking a second thing replaces the first rather than being turned away.
 *
 * ## Where each pick goes
 *
 * The dialog resolves nothing itself; it emits {@link MediaPick}s and the drawer
 * does the network work, so one spinner and one error line serve every source.
 * The routing rule is legacy's `uploadProcess` (crud-content.component.ts:442-455):
 * My Media and Giphy attach as they stand, a stock photo is imported into
 * Cloudinary by URL, a device file is uploaded.
 *
 * ## Why `<ui-modal>` and not a native `<dialog>` of its own
 *
 * Every other dialog this drawer raises is a `<ui-modal>`; the drawer itself is
 * the exception because the library panel is a fixed 400px box and the sheet is
 * a 1360×944 two-column layout. This one takes the `::ng-deep` the drawer
 * declined — a browsable grid needs the width, and one selector against
 * `.ui-modal__panel`, scoped under `:host`, is a much smaller thing than a
 * hand-rolled dialog.
 *
 * ## What is validated, and what is not
 *
 * Type and size come from the shared rulebook in `./media-rules` —
 * {@link ACCEPTED_TYPES} and {@link MAX_FILE_BYTES}, the same constants the
 * users app's `MediaUploadService` gates on, rather than a second copy of the
 * numbers here. Count is this dialog's own ({@link MAX_MEDIA_ITEMS}).
 *
 * The rest of the users app's *post-upload* image pipeline — Compressor.js at
 * quality 0.6, the 36 MP / 2048px resolution gates, the Instagram aspect-ratio
 * padding and the optimisation modal — now lives in this library too
 * (`./image-upload`, `./image-validation`) but this dialog still does not run
 * it: the drawer, not the picker, owns the upload, and every one of those paths
 * ends in a Cloudinary asset being deleted or replaced. Legacy's GIF warning is
 * not ported either, for the simplest reason: it is commented out in
 * `media-modal.component.html:184-195` and its `@ViewChild` always resolves to
 * `undefined`.
 *
 * ## One thing the host app has to provide
 *
 * The grid names each tile with `<span class="sr-only">`, and `.sr-only` ships
 * in `@sisoma/ui`'s `foundations`, not here — a library has no global sheet to
 * put it in. So a host app must load `@use 'foundations';` in its own
 * `styles.scss`, as `partner-area` and `admin` both do. `@sisoma/auth` depends
 * on the same thing for its `sso-denials` caption. An app without it would
 * render every tile's filename as visible text under the thumbnail, which is
 * the failure HAL-145 found on the members table.
 */
@Component({
  selector: 'app-media-picker',
  imports: [Button, FeedbackBanner, Icon, InputControl, Modal, SearchInput, Tab, Tabs],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MediaLibraryService],
  templateUrl: './media-picker.html',
  styleUrls: ['./media-picker.scss', './media-picker-grid.scss'],
})
export class MediaPicker {
  /** What the item's post type accepts. */
  readonly kind = input.required<MediaKind>();

  /** How many media the item already has — the {@link MAX_MEDIA_ITEMS} cap counts them. */
  readonly attached = input(0);

  /**
   * Take exactly one thing, replacing whatever is chosen when a second is
   * picked — the rule a `document` post already lives under, offered to callers
   * whose pick is singular for a different reason (the design card's feature
   * image, which sets one url and attaches nothing).
   *
   * A boolean rather than a `maxSelections: number`: the only cap anyone needs
   * above one is {@link MAX_MEDIA_ITEMS}, which the dialog already applies, and
   * "replace on add" is a rule that only reads sensibly at one. This flag routes
   * into the same {@link singleSelect} branch the document rule uses, so the
   * document behaviour is unchanged by construction rather than by care.
   */
  readonly single = input(false, { transform: booleanAttribute });

  /**
   * Overrides the dialog's own title. The default names the *action* the dialog
   * usually performs ("Add Media"); a caller whose action is something else —
   * setting a feature image — says so here.
   */
  readonly heading = input<string | null>(null);

  /**
   * The public ids already on the item. A library tile matching one is shown as
   * added and cannot be picked twice — the drawer's own list is keyed on
   * `mediaPublicId`, so a duplicate would be a second card for one asset.
   */
  readonly attachedIds = input<readonly string[]>([]);

  /** What was chosen. The drawer resolves each pick; this dialog never does. */
  readonly picked = output<readonly MediaPick[]>();

  /** Closed without choosing anything. */
  readonly closed = output<void>();

  private readonly library = inject(MediaLibraryService);

  protected readonly open = signal(true);
  protected readonly tab = signal<TabId>('upload');

  /** Files from the Upload tab. */
  protected readonly files = signal<readonly PickedFile[]>([]);
  /** Tiles chosen on any library tab, in the order they were chosen. */
  protected readonly chosen = signal<readonly LibraryItem[]>([]);
  protected readonly dragging = signal(false);
  /** Why the last drop, selection or click was turned away, if it was. */
  protected readonly rejected = signal<string | null>(null);

  // --- The current library tab's page ---------------------------------------

  protected readonly items = signal<readonly LibraryItem[]>([]);
  protected readonly loading = signal(false);
  protected readonly loadError = signal<string | null>(null);
  /** The committed search term. Blank means "browse the featured set". */
  protected readonly term = signal('');
  /** What is in the box, which the debounce turns into {@link term}. */
  protected readonly searchDraft = signal('');
  private readonly nextCursor = signal<string | null>(null);

  /** Bumped per request; a reply whose token is stale is dropped. */
  private request = 0;
  private readonly typed = new Subject<string>();

  protected readonly isDocument = computed(() => this.kind() === 'document');

  protected readonly modalHeading = computed(
    () => this.heading() ?? (this.isDocument() ? 'Add Document' : 'Add Media'),
  );

  /**
   * One thing only: legacy's `isDocument` rule, or a caller that asked for it
   * ({@link single}). Everything downstream — the file input, the cap, the
   * replace-on-add collapse — reads this rather than either half.
   */
  protected readonly singleSelect = computed(() => this.isDocument() || this.single());

  /** A document replaces rather than accumulates — legacy's `isDocument` rule. */
  protected readonly multiple = computed(() => !this.singleSelect());

  protected readonly accept = computed(() => ACCEPTED_TYPES[this.kind()].join(','));

  protected readonly hint = computed(() => HINTS[this.kind()]);

  /** The tab strip, per the table in the class doc. */
  protected readonly tabs = computed<readonly { id: TabId; label: string }[]>(() => {
    const sources: LibrarySource[] =
      this.kind() === 'image'
        ? ['usersMedia', 'pexels', 'unsplash', 'pixabay']
        : this.kind() === 'video'
          ? ['usersMedia', 'giphy']
          : ['usersMedia'];

    return [
      { id: 'upload' as const, label: 'Upload' },
      ...sources.map((id) => ({ id: id as TabId, label: SOURCE_LABELS[id] })),
    ];
  });

  protected readonly onUpload = computed(() => this.tab() === 'upload');

  /** My Media has no search box in legacy either (media-library.component.html:6). */
  protected readonly searchable = computed(() => !this.onUpload() && this.tab() !== 'usersMedia');

  protected readonly credit = computed(() => CREDITS[this.tab()] ?? null);

  /** `placeholder="Search {{ selectedLibrary }}"` — media-library.component.html:12. */
  protected readonly searchPlaceholder = computed(() => {
    const tab = this.tab();
    return tab === 'upload' ? 'Search' : `Search ${SOURCE_LABELS[tab]}`;
  });

  protected readonly hasMore = computed(() => this.nextCursor() !== null);

  /** How many more things this dialog will take. */
  protected readonly remaining = computed(() => {
    const taken = this.files().length + this.chosen().length;
    return this.singleSelect()
      ? Math.max(0, 1 - taken)
      : Math.max(0, MAX_MEDIA_ITEMS - this.attached() - taken);
  });

  /** Everything chosen so far, whichever tab put it there. */
  protected readonly selection = computed<readonly SelectedEntry[]>(() => [
    ...this.files().map((entry, index) => ({
      key: `file:${index}:${entry.file.name}`,
      name: entry.file.name,
      thumbUrl: entry.previewUrl,
      glyph: (this.isDocument() ? 'file-text' : 'video') as SelectedEntry['glyph'],
      list: 'files' as const,
      index,
    })),
    ...this.chosen().map((item, index) => ({
      key: `library:${item.key}`,
      name: item.name,
      thumbUrl: item.thumbUrl,
      glyph: 'image' as SelectedEntry['glyph'],
      list: 'library' as const,
      index,
    })),
  ]);

  protected readonly canAdd = computed(() => this.selection().length > 0);

  protected readonly addLabel = computed(() => {
    const count = this.selection().length;
    if (this.isDocument()) return 'Add document';
    // A single pick is not being *added* to anything the caller keeps a list of
    // — the confirm button says what it does with the one thing chosen.
    if (this.single()) return `Use ${KIND_NOUNS[this.kind()]}`;
    return count > 1 ? `Add ${count} files` : 'Add file';
  });

  protected readonly emptyMessage = computed(() =>
    this.tab() === 'usersMedia'
      ? `Nothing here yet — anything you upload is added to your ${SOURCE_LABELS.usersMedia}.`
      : 'No results. Try a different search.',
  );

  constructor() {
    inject(DestroyRef).onDestroy(() => this.revokeAll());

    // A genuine event stream, so RxJS rather than a signal: the keystrokes are
    // rate-limited, the committed term is not.
    this.typed
      .pipe(debounceTime(SEARCH_DEBOUNCE_MS), takeUntilDestroyed())
      .subscribe((value) => this.commitSearch(value));
  }

  // --- Tabs ------------------------------------------------------------------

  protected selectTab(id: string | undefined): void {
    if (!id || id === this.tab()) return;
    // Legacy clears every data set on each tab switch (media-library.component.ts:103);
    // the selection survives, the page does not.
    this.request++;
    this.items.set([]);
    this.nextCursor.set(null);
    this.loadError.set(null);
    this.loading.set(false);
    this.term.set('');
    this.searchDraft.set('');
    this.rejected.set(null);
    this.tab.set(id as TabId);
    void this.load(id as TabId, this.kind(), false);
  }

  // --- Search ----------------------------------------------------------------

  protected onSearchInput(value: string): void {
    this.searchDraft.set(value);
    this.typed.next(value);
  }

  /** Enter commits at once, as legacy's only search trigger does. */
  protected onSearchEnter(): void {
    this.commitSearch(this.searchDraft());
  }

  private commitSearch(value: string): void {
    const next = value.trim();
    if (next === this.term()) return;
    this.term.set(next);
    void this.load(this.tab(), this.kind(), false);
  }

  // --- Paging ----------------------------------------------------------------

  protected loadMore(): void {
    void this.load(this.tab(), this.kind(), true);
  }

  protected retry(): void {
    void this.load(this.tab(), this.kind(), false);
  }

  private async load(tab: TabId, kind: MediaKind, more: boolean): Promise<void> {
    if (tab === 'upload') return;
    const cursor = more ? this.nextCursor() : null;
    if (more && cursor === null) return;

    const token = ++this.request;
    this.loading.set(true);
    this.loadError.set(null);

    try {
      const page =
        tab === 'usersMedia'
          ? await this.library.myMedia(kind, cursor)
          : await this.library.stock(tab, { query: this.term(), cursor });

      if (token !== this.request) return;
      this.items.update((list) => (more ? [...list, ...page.items] : [...page.items]));
      this.nextCursor.set(page.next);
    } catch (error) {
      if (token !== this.request) return;
      this.loadError.set(apiErrorMessage(error, `Could not load ${SOURCE_LABELS[tab]}.`));
    } finally {
      if (token === this.request) this.loading.set(false);
    }
  }

  // --- Library selection ------------------------------------------------------

  protected isChosen(item: LibraryItem): boolean {
    return this.chosen().some((entry) => entry.key === item.key);
  }

  /** Already on the content item, so it is shown as added rather than offered. */
  protected isAttached(item: LibraryItem): boolean {
    return !!item.mediaPublicId && this.attachedIds().includes(item.mediaPublicId);
  }

  protected toggle(item: LibraryItem): void {
    if (this.isAttached(item)) return;
    this.rejected.set(null);

    if (this.isChosen(item)) {
      this.chosen.update((list) => list.filter((entry) => entry.key !== item.key));
      return;
    }

    // Legacy's `addSelectedMedia` collapses to a single item for a document
    // (media-modal.component.ts:156-158); the Upload tab's file goes with it.
    // A caller-declared single pick collapses the same way.
    if (this.singleSelect()) {
      this.revokeAll();
      this.files.set([]);
      this.chosen.set([item]);
      return;
    }

    if (!this.remaining()) {
      this.rejected.set(`Content can carry up to ${MAX_MEDIA_ITEMS} media.`);
      return;
    }

    this.chosen.update((list) => [...list, item]);
  }

  // --- The Upload tab ---------------------------------------------------------

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(true);
  }

  protected onDragLeave(): void {
    this.dragging.set(false);
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(false);
    // `Array.from` rather than a spread: a `FileList` is only iterable under
    // `lib: dom.iterable`, and the users app compiles with plain `lib: dom`.
    this.accepted(Array.from(event.dataTransfer?.files ?? []));
  }

  protected onSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.accepted(Array.from(input.files ?? []));
    // So re-picking the same file fires `change` again.
    input.value = '';
  }

  // --- The Selected strip -----------------------------------------------------

  protected remove(entry: SelectedEntry): void {
    this.rejected.set(null);
    if (entry.list === 'library') {
      this.chosen.update((list) => list.filter((_, i) => i !== entry.index));
      return;
    }

    const picked = this.files()[entry.index];
    if (picked?.previewUrl) URL.revokeObjectURL(picked.previewUrl);
    this.files.update((list) => list.filter((_, i) => i !== entry.index));
  }

  protected confirm(): void {
    if (!this.canAdd()) return;
    this.picked.emit([
      ...this.files().map((entry): MediaPick => ({ from: 'device', file: entry.file })),
      ...this.chosen().map((item) => item.pick),
    ]);
    this.open.set(false);
  }

  protected cancel(): void {
    this.open.set(false);
  }

  protected onModalClosed(): void {
    this.closed.emit();
  }

  /**
   * Sort a batch into what is kept and why the rest was not, then report the
   * first reason. Reporting one line rather than one per file keeps the dialog
   * the size it is; the file that survived is visible either way.
   */
  private accepted(incoming: File[]): void {
    if (!incoming.length) return;

    const noun = KIND_NOUNS[this.kind()];
    const kept: PickedFile[] = [];
    let reason: string | null = null;

    // A document is single, so a new file displaces whatever is already
    // selected — the library pick AND a previously chosen file, the same
    // replace-on-add the drawer applies to the attachment itself.
    if (this.singleSelect() && (this.chosen().length || this.files().length)) {
      this.revokeAll();
      this.files.set([]);
      this.chosen.set([]);
    }

    for (const file of incoming) {
      if (!ACCEPTED_TYPES[this.kind()].includes(file.type)) {
        reason ??= `${file.name} isn't a supported ${noun} — ${this.hint()}.`;
        continue;
      }
      if (file.size > MAX_FILE_BYTES[this.kind()]) {
        reason ??= `${file.name} is over the ${megabytes(MAX_FILE_BYTES[this.kind()])} limit.`;
        continue;
      }
      if (kept.length >= this.remaining()) {
        reason ??= this.isDocument()
          ? 'A document post takes one file.'
          : this.single()
            ? `Choose one ${noun}.`
            : `Content can carry up to ${MAX_MEDIA_ITEMS} media.`;
        continue;
      }
      kept.push({ file, previewUrl: previewUrlFor(file) });
    }

    this.rejected.set(reason);
    if (kept.length) this.files.update((files) => [...files, ...kept]);
  }

  private revokeAll(): void {
    for (const entry of this.files()) {
      if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    }
  }
}

/** Images preview locally before they upload; nothing else has a frame to show. */
function previewUrlFor(file: File): string | null {
  // jsdom has no createObjectURL, and a preview is decoration — never let its
  // absence lose the file.
  if (!file.type.startsWith('image/') || typeof URL.createObjectURL !== 'function') return null;
  return URL.createObjectURL(file);
}
